import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Box,
    Typography,
    Alert,
    IconButton,
    Toolbar,
    AppBar,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import { GetOrderDocument } from "../../services/OrderService";
import CommonLoader from "../common/CommonLoader";

// fetchDocument lets other modules (e.g. supply orders) reuse this viewer with their own endpoint.
const DocumentViewer = ({ fetchDocument: loadDocument = GetOrderDocument }) => {
    const { orderNumber, documentTitle } = useParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.down("md"));

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [fileUrl, setFileUrl] = useState(null);
    const [contentType, setContentType] = useState("");

    useEffect(() => {
        fetchDocument();
    }, [orderNumber, documentTitle]);

    useEffect(() => {
        return () => {
            if (fileUrl) {
                URL.revokeObjectURL(fileUrl);
            }
        };
    }, [fileUrl]);

    const fetchDocument = async () => {
        try {
            setLoading(true);
            setError("");

            const blob = await loadDocument(documentTitle, orderNumber);
            const blobUrl = URL.createObjectURL(blob);

            setFileUrl(blobUrl);
            setContentType(blob.type || "");
        } catch (err) {
            console.error("Error loading document:", err);
            setError("Failed to load document. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        window.close();
        setTimeout(() => {
            window.history.back();
        }, 100);
    };

    const handleDownload = () => {
        if (fileUrl) {
            const link = document.createElement("a");
            link.href = fileUrl;
            link.download = documentTitle;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    // Calculate toolbar height based on device
    const toolbarHeight = isMobile ? 56 : 64;

    const renderViewer = () => {
        if (!fileUrl) return null;

        // PDF Viewer
        if (contentType.includes("pdf") || documentTitle.toLowerCase().endsWith(".pdf")) {
            return (
                <Box
                    sx={{
                        width: "100%",
                        height: `calc(100vh - ${toolbarHeight}px)`,
                        bgcolor: "#525659",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        overflow: "hidden",
                    }}
                >
                    <iframe
                        src={fileUrl}
                        title="PDF Viewer"
                        style={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                        }}
                    />
                </Box>
            );
        }

        // Image Viewer
        if (
            contentType.startsWith("image/") ||
            /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(documentTitle)
        ) {
            return (
                <Box
                    sx={{
                        width: "100%",
                        height: `calc(100vh - ${toolbarHeight}px)`,
                        bgcolor: "#1e1e1e",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        overflow: "auto",
                        p: isMobile ? 1 : 2,
                    }}
                >
                    <Box
                        component="img"
                        src={fileUrl}
                        alt="Document"
                        sx={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
                            touchAction: "pinch-zoom", // Enable pinch-to-zoom on mobile
                        }}
                    />
                </Box>
            );
        }

        // Text/HTML Viewer
        if (contentType.includes("text") || contentType.includes("html")) {
            return (
                <Box
                    sx={{
                        width: "100%",
                        height: `calc(100vh - ${toolbarHeight}px)`,
                        bgcolor: "#f5f5f5",
                    }}
                >
                    <iframe
                        src={fileUrl}
                        title="Document Viewer"
                        style={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                        }}
                    />
                </Box>
            );
        }

        // Default viewer for other types
        return (
            <Box
                sx={{
                    width: "100%",
                    height: `calc(100vh - ${toolbarHeight}px)`,
                    bgcolor: "#f5f5f5",
                }}
            >
                <iframe
                    src={fileUrl}
                    title="Document Viewer"
                    style={{
                        width: "100%",
                        height: "100%",
                        border: "none",
                    }}
                />
            </Box>
        );
    };

    return (
        <Box 
            sx={{ 
                height: "100vh", 
                display: "flex", 
                flexDirection: "column",
                overflow: "hidden",
            }}
        >
            {/* Header AppBar */}
            <AppBar position="static" elevation={1}>
                <Toolbar variant={isMobile ? "dense" : "regular"}>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={handleClose}
                        aria-label="close"
                        sx={{ mr: isMobile ? 1 : 2 }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography 
                        variant={isMobile ? "body1" : "h6"} 
                        component="div" 
                        sx={{ 
                            flexGrow: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: isMobile ? "0.9rem" : "1.25rem",
                        }}
                    >
                        {documentTitle}
                    </Typography>
                    <IconButton
                        color="inherit"
                        onClick={handleDownload}
                        aria-label="download"
                        disabled={!fileUrl}
                    >
                        <DownloadIcon fontSize={isMobile ? "small" : "medium"} />
                    </IconButton>
                </Toolbar>
            </AppBar>

            {/* Content Area */}
            <Box sx={{ flex: 1, overflow: "hidden", position: "relative" }}>
                {loading && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                            bgcolor: "#f5f5f5",
                        }}
                    >
                        <CommonLoader loading={loading} />
                    </Box>
                )}

                {error && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                            p: isMobile ? 2 : 3,
                        }}
                    >
                        <Alert 
                            severity="error" 
                            sx={{ 
                                maxWidth: isMobile ? "100%" : 600,
                                width: "100%",
                            }}
                        >
                            {error}
                        </Alert>
                    </Box>
                )}

                {!loading && !error && renderViewer()}
            </Box>
        </Box>
    );
};

export default DocumentViewer;